// src/api/authClient.ts
// Session-cookie auth client.

import { AUTH_401_EVENT } from './authEvents';
import { readCookie } from './cookieUtil';

// Pairs with backend scripts/fastapi_pkg/_session_auth_wire.py:
//   POST /api/auth/login   form-encoded {user, password} → 200 + sets cookies
//   POST /api/auth/logout
//   GET  /api/auth/me      → 401 if no session
//   GET  /api/auth/status  → always 200, {authenticated, user, configured}
//   GET  /api/auth/csrf    → returns {csrf} for the current session (401 if none)
//
// Cookies set by server:
//   dash_sid   HttpOnly  signed session id; JS cannot read.
//   dash_csrf  readable  double-submit CSRF token; we mirror it into header
//              X-CSRF-Token on every write.
//
// All API calls funnel through fetchWithAuth() which auto-injects the CSRF
// header on write methods and redirects to /login on 401.

const SESSION_COOKIE = 'dash_sid';
const CSRF_COOKIE = 'dash_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const TRACE_HEADER = 'X-Trace-Id';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

// ── MES trace ring buffer ────────────────────────────────────────────────
// Every response sets X-Trace-Id; we tail the last N so the user can pick
// a trace from a dropdown without copy-pasting UUIDs.
interface TraceEntry { trace_id: string; path: string; status: number; ts_ms: number; }
const TRACE_RING_MAX = 50;
const _traceRing: TraceEntry[] = [];

export function recentTraces(): TraceEntry[] {
  return _traceRing.slice().reverse();  // newest first
}

function _recordTrace(headers: Headers, url: string, status: number): void {
  const tid = headers.get(TRACE_HEADER);
  if (!tid) return;
  _traceRing.push({ trace_id: tid, path: url, status, ts_ms: Date.now() });
  if (_traceRing.length > TRACE_RING_MAX) _traceRing.shift();
}

export function hasSessionCookie(): boolean {
  // Note: dash_sid is HttpOnly so we cannot read it from JS — use dash_csrf as
  // the JS-visible proxy: server sets both together and clears both together.
  return readCookie(CSRF_COOKIE) !== '';
}

/** Drop-in fetch replacement that handles auth concerns. */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set(CSRF_HEADER, csrf);
  }

  const res = await fetch(input, {
    ...init,
    method,
    headers,
    credentials: 'include',
  });

  _recordTrace(res.headers, typeof input === 'string' ? input : String(input), res.status);

  if (res.status === 401) {
    // V12 Navigation Authority 2026-06-12:
    // authClient is a SIGNAL emitter, not a navigation controller.
    // window.location is the SSOT; Router (routes.tsx) is the sole
    // owner of navigation. This preserves runtime state (FrameCache,
    // WS stream, ranking/positions) — see core/runtime.ts.
    window.dispatchEvent(new CustomEvent(AUTH_401_EVENT, {
      detail: { from: window.location.pathname + window.location.search },
    }));
  }
  return res;
}

export interface AuthStatus {
  authenticated: boolean;
  user: string | null;
  configured: boolean;
}

export const authClient = {
  /** Public status probe (does NOT redirect on 401). */
  async status(): Promise<AuthStatus> {
    const r = await fetch('/api/auth/status', { credentials: 'include' });
    if (!r.ok) return { authenticated: false, user: null, configured: false };
    return r.json();
  },

  /** Returns {user} or throws. */
  async me(): Promise<{ user: string }> {
    const r = await fetchWithAuth('/api/auth/me');
    if (!r.ok) throw new Error('not authenticated');
    return r.json();
  },

  /** Returns the csrf token for the current session. */
  async csrf(): Promise<string> {
    const r = await fetchWithAuth('/api/auth/csrf');
    if (!r.ok) throw new Error('no session');
    const j = await r.json();
    return j.csrf as string;
  },

  /**
   * form-encoded POST. On 200, cookies are set by the server.
   * On failure, throws an Error with:
   *   .status     HTTP status (0 for network failure)
   *   .retryAfter seconds to wait, if the server returned 429 with
   *               "wait Ns" in the detail message; otherwise undefined.
   */
  async login(user: string, password: string): Promise<{ ok: boolean; user: string }> {
    const body = new URLSearchParams({ user, password }).toString();
    let r: Response;
    try {
      r = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body,
      });
    } catch (e) {
      // Network failure: no Response at all.
      const err = new Error('backend unreachable') as Error & { status: number; retryAfter?: number };
      err.status = 0;
      throw err;
    }
    if (!r.ok) {
      const detail = await r.json().catch(() => ({ detail: r.statusText }));
      const err = new Error(detail.detail || `login failed: ${r.status}`) as Error & { status: number; retryAfter?: number };
      err.status = r.status;
      if (r.status === 429 && typeof detail.detail === 'string') {
        const m = detail.detail.match(/wait\s+(\d+)\s*s/i);
        if (m) err.retryAfter = parseInt(m[1], 10);
      }
      throw err;
    }
    return r.json();
  },

  /** Clears session both client- and server-side. Always succeeds. */
  async logout(): Promise<void> {
    try {
      await fetchWithAuth('/api/auth/logout', { method: 'POST' });
    } catch {
      // ignore — even on network failure we treat as "logged out locally"
    }
  },
};
